// Cho thuê / Bán selector for the property list page — the most-used
// filter, promoted out of the advanced filter dialog so it's always
// visible instead of hidden behind "Bộ lọc nâng cao".
//
// Tri-state (unlike PropertyViewToggle's strict 2-state): "Tất cả" is a
// first-class option here because `listingType` is optional end-to-end in
// property-filter-params.ts, and the advanced dialog no longer has a
// manual field for it — without a visible "Tất cả" pill there'd be no way
// back to "show everything" once a type is picked.
//
// State persists in `?listingType=SALE|RENT` (absent = "Tất cả"), matching
// how the advanced dialog and AI extractor already read/write this param.

import { useSearchParams } from "react-router";

import { cn } from "@/lib/utils";
import { readFilterDraft } from "@/features/property/utils/property-filter-params";
import type { ListingType } from "@/types/property";

const ANY_VALUE = "ANY";

type OptionValue = ListingType | typeof ANY_VALUE;

const OPTIONS: ReadonlyArray<{ value: OptionValue; label: string }> = [
  { value: ANY_VALUE, label: "Tất cả" },
  { value: "SALE", label: "Bán" },
  { value: "RENT", label: "Cho thuê" },
];

interface PropertyListingTypeToggleProps {
  className?: string;
  variant?: "desktop" | "mobile";
}

export function PropertyListingTypeToggle({
  className,
  variant = "desktop",
}: PropertyListingTypeToggleProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const value: OptionValue = readFilterDraft(searchParams).listingType ?? ANY_VALUE;

  const setValue = (next: OptionValue) => {
    if (next === value) return;
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === ANY_VALUE) {
          out.delete("listingType");
        } else {
          out.set("listingType", next);
        }
        out.delete("page");
        return out;
      },
      { replace: true },
    );
  };

  return (
    <div
      role="tablist"
      aria-label="Loại giao dịch"
      className={cn(
        "inline-flex items-center gap-1 rounded-[10px] border border-border p-[3px]",
        variant === "mobile" ? "bg-card" : "h-[34px] bg-background",
        className,
      )}
    >
      {OPTIONS.map((o) => {
        const isActive = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setValue(o.value)}
            className={cn(
              "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              variant === "mobile"
                ? "rounded-[7px] px-2 py-1.5 text-[11px]"
                : "h-full rounded-[6px] px-2.5 text-[12px]",
              isActive
                ? "bg-foreground font-semibold text-background"
                : "font-medium text-muted-foreground hover:text-foreground",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
